import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import SidebarNav from '../components/SidebarNav';
import TopTabs from '../components/common/TopTabs';
import { LayoutOutletContext } from '../components/Layout';
import { useAppState } from '../context/AppStateContext';
import useUrlTab from '../hooks/useUrlTab';
import { ReportTemplateDataService } from '../services/reportTemplateDataService';
import { apiErrorMessage } from '../services/supportDataService';
import {
  SupportDiagnosticsService, AiPlan, AiStatus, DiagAttention, DiagError, DiagImage, DiagPost, DiagPostFilters, DiagTextFacts,
  DiagReport, DiagReportFilters, useSupportDiagnosticsAllowed,
} from '../services/supportDiagnosticsService';

/**
 * Rustle It support: the status of posts, images and reports, never their
 * content (tdp-api docs/support-access-plan.md §9a). Only in a support session
 * whose grant carries the Admin's opt-in; the API enforces that, this page
 * just says so plainly instead of showing a wall of refusals.
 *
 * Three ways in: what needs attention (no information needed), posts by what
 * the Admin can describe (date, author, child code, classroom, or P- refs),
 * and reports (period, child code, template, or R- refs). One post opens in
 * place, with its images as yes/no states and its recorded errors.
 */

type Tab = 'attention' | 'posts' | 'reports';
const TABS: readonly Tab[] = ['attention', 'posts', 'reports'];

/** Timestamps arrive as ISO strings; shown in the browser's time zone. */
function when(raw: string | null | undefined): string {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** Calendar dates (post date, report period). */
function day(raw: string | null | undefined): string {
  if (!raw) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (!m) return raw;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const YesNo: React.FC<{ value: boolean | null | undefined; bad?: boolean }> = ({ value, bad = true }) => {
  if (value === null || value === undefined) return <span className="text-muted">—</span>;
  return value
    ? <span className="support-diag__yes"><i className="fas fa-check" aria-hidden="true" /> Yes</span>
    : <span className={bad ? 'support-diag__no' : 'text-muted'}><i className="fas fa-xmark" aria-hidden="true" /> No</span>;
};

const Note: React.FC<{ text?: string }> = ({ text }) =>
  text ? <p className="support-diag__note"><i className="fas fa-circle-info" aria-hidden="true" /> {text}</p> : null;

// ── AI processing ─────────────────────────────────────────────────────────

const AI_STATUS: Record<AiStatus, { label: string; title: string; cls: string }> = {
  done: { label: 'Done', title: 'The AI rewrite was made.', cls: 'support-diag__yes' },
  skipped: {
    label: 'Skipped',
    title: 'Deliberately not analysed: too short, or judged not worth analysing. Normal. (A failure during an outage may also look like this, as errors are sampled then.)',
    cls: 'text-muted',
  },
  failed: { label: 'Failed', title: 'An AI processing error was recorded for this post.', cls: 'support-diag__no' },
  not_run: { label: 'Not run', title: 'No AI processing yet. Normal for a few minutes after saving.', cls: 'support-diag__no' },
};

const AiStatusText: React.FC<{ status: AiStatus }> = ({ status }) => {
  const s = AI_STATUS[status];
  return <span className={s.cls} title={s.title}>{s.label}</span>;
};

const PlanLine: React.FC<{ plan: AiPlan }> = ({ plan }) => (
  <p className="support-diag__plan">
    <i className="fas fa-wand-magic-sparkles" aria-hidden="true" />{' '}
    {plan === 'full'
      ? <><strong>AI plan: Full AI.</strong> Each post gets an AI rewrite, tags and a search index.</>
      : <><strong>AI plan: Rewrite only.</strong> Each post gets an AI rewrite. No tags or search index are made, so none are shown.</>}
  </p>
);

// ── Errors ────────────────────────────────────────────────────────────────

const ErrorsTable: React.FC<{ errors: DiagError[]; onOpenPost?: (ref: string) => void; empty: string }> = (
  { errors, onOpenPost, empty },
) => {
  if (errors.length === 0) return <p className="text-muted mb-0">{empty}</p>;
  return (
    <div className="table-responsive">
      <table className="table table-sm support-diag__table">
        <thead>
          <tr><th>When</th><th>Event</th><th>Error</th><th>Item</th><th>Children</th><th>Request ID</th></tr>
        </thead>
        <tbody>
          {errors.map((e, i) => (
            <tr key={`${e.requestId ?? ''}-${i}`}>
              <td className="text-nowrap">{when(e.at)}</td>
              <td><code>{e.code}</code></td>
              <td>{[e.errName, e.errCode].filter(Boolean).join(' · ') || '—'}</td>
              <td className="text-nowrap">
                {e.post && (onOpenPost
                  ? <button type="button" className="btn btn-link btn-sm p-0" onClick={() => onOpenPost(e.post!)}>{e.post}</button>
                  : e.post)}
                {e.uploadId && <span className="text-muted"> image #{e.uploadId}</span>}
                {e.report && <span>{e.report}</span>}
                {e.templateId && <span className="text-muted"> template #{e.templateId}</span>}
                {e.period && <div className="text-muted small">{day(e.period.start)} – {day(e.period.end)}</div>}
                {!e.post && !e.report && !e.uploadId && !e.templateId && !e.period && '—'}
              </td>
              <td>{e.children.length ? e.children.join(', ') : '—'}</td>
              <td>{e.requestId ? <code className="support-diag__reqid">{e.requestId}</code> : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Posts ─────────────────────────────────────────────────────────────────

const PostsTable: React.FC<{ posts: DiagPost[]; plan: AiPlan; classroomName: (id: number | null) => string; onOpen: (ref: string) => void }> = (
  { posts, plan, classroomName, onOpen },
) => (
  <div className="table-responsive">
    <table className="table table-sm table-hover support-diag__table">
      <thead>
        <tr>
          <th>Ref</th><th>Post date</th><th>Author</th><th>Classroom</th><th>Status</th><th>Children</th><th>Images</th><th>AI processing</th>
          {plan === 'full' && <><th>Tags</th><th>Search index</th></>}
        </tr>
      </thead>
      <tbody>
        {posts.map(p => (
          <tr key={p.id} className="support-diag__row" onClick={() => onOpen(p.ref)}>
            <td><button type="button" className="btn btn-link btn-sm p-0" onClick={e => { e.stopPropagation(); onOpen(p.ref); }}>{p.ref}</button></td>
            <td className="text-nowrap">{day(p.postDate)}</td>
            <td>{p.author?.name ?? '—'}</td>
            <td>{classroomName(p.classroomId)}</td>
            <td>{p.status === 'published' ? 'Published' : 'Not published'}</td>
            <td>{p.children.length ? p.children.join(', ') : '—'}</td>
            <td className="text-nowrap">
              {p.images.count}
              {p.images.missingThumbnail > 0 && (
                <span className="support-diag__no"> · {p.images.missingThumbnail} without thumbnail</span>
              )}
            </td>
            <td><AiStatusText status={p.ai.status} /></td>
            {plan === 'full' && (
              <>
                <td>{p.ai.status === 'done' ? <YesNo value={p.ai.tags} bad={false} /> : <span className="text-muted">—</span>}</td>
                <td>{p.ai.status === 'done' ? <YesNo value={p.ai.searchIndex} /> : <span className="text-muted">—</span>}</td>
              </>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PostDetail: React.FC<{ postRef: string; classroomName: (id: number | null) => string; onBack: () => void }> = (
  { postRef, classroomName, onBack },
) => {
  const [data, setData] = useState<Awaited<ReturnType<typeof SupportDiagnosticsService.post>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setError(null);
    SupportDiagnosticsService.post(postRef)
      .then(setData)
      .catch(err => setError(apiErrorMessage(err, 'Could not load this post.')));
  }, [postRef]);

  return (
    <div className="support-diag__detail">
      <button type="button" className="btn btn-link btn-sm px-0 mb-2" onClick={onBack}>
        <i className="fas fa-arrow-left" aria-hidden="true" /> Back
      </button>
      {error && <div className="alert alert-warning">{error}</div>}
      {!data && !error && <p className="text-muted">Loading…</p>}
      {data && (() => {
        const p = data.post;
        return (
          <>
            <h2 className="support-diag__title">Post {p.ref}</h2>
            <dl className="support-diag__facts">
              <dt>Post date</dt><dd>{day(p.postDate)}</dd>
              <dt>Status</dt><dd>{p.status === 'published' ? 'Published' : 'Not published'}</dd>
              <dt>Author</dt><dd>{p.author?.name ?? '—'}</dd>
              <dt>Classroom</dt><dd>{classroomName(p.classroomId)}</dd>
              <dt>Children</dt><dd>{p.children.length ? p.children.join(', ') : '—'}</dd>
              <dt>Created</dt><dd>{when(p.createdAt)}</dd>
              <dt>Last changed</dt><dd>{when(p.updatedAt)}</dd>
              <dt>AI processing</dt>
              <dd>
                <AiStatusText status={p.ai.status} />
                {p.ai.processedAt && <span className="text-muted"> · {when(p.ai.processedAt)}</span>}
                <div className="text-muted small">{AI_STATUS[p.ai.status].title}</div>
              </dd>
              {data.aiPlan === 'full' && p.ai.status === 'done' && (
                <>
                  <dt>Tags</dt><dd><YesNo value={p.ai.tags} bad={false} />{p.ai.tags === false ? <span className="text-muted"> (none found in the text)</span> : null}</dd>
                  <dt>Search index</dt><dd><YesNo value={p.ai.searchIndex} /></dd>
                </>
              )}
            </dl>
            <PlanLine plan={data.aiPlan} />

            <h3 className="support-diag__subtitle">Text</h3>
            <TextFactsTable facts={data.text} />
            <p className="support-diag__hint">
              Facts about the text, never the text. Child names are stored as placeholders such as [child_name]
              and swapped back when someone reads the post; any other [bracketed] word would show to staff as a
              broken name.
            </p>

            <h3 className="support-diag__subtitle">Images ({data.images.length})</h3>
            {data.images.length === 0 ? <p className="text-muted">No images.</p> : <ImagesTable images={data.images} />}

            <h3 className="support-diag__subtitle">Recorded errors</h3>
            <ErrorsTable errors={data.errors} empty="No errors recorded for this post." />
            <Note text={data.note} />
          </>
        );
      })()}
    </div>
  );
};

const TEXT_FIELD: Record<DiagTextFacts['field'], string> = {
  text: "Staff member's text",
  published: 'Published text',
  ai_rewrite: 'AI rewrite',
};

/** A count, highlighted when non-zero and `flag` says that's worth a look. */
const Count: React.FC<{ n: number | null; flag?: boolean }> = ({ n, flag = true }) =>
  n === null ? <span className="text-muted">—</span> : <span className={flag && n > 0 ? 'support-diag__no' : undefined}>{n}</span>;

const TextFactsTable: React.FC<{ facts: DiagTextFacts[] }> = ({ facts }) => (
  <div className="table-responsive">
    <table className="table table-sm support-diag__table">
      <thead>
        <tr>
          <th>Text</th><th>Characters</th><th>Words</th><th>Lines</th><th>Emoji</th><th>Invisible</th>
          <th>Other unusual</th>
          <th title="Tags such as <br/> or </p>, and codes such as &amp;, as pasted from a web page or email">HTML tags</th>
          <th title="A tag, link or handler that could run or load something, such as <script> or onerror=">Possible script</th>
          <th>Name placeholders</th><th>Other [tokens]</th>
        </tr>
      </thead>
      <tbody>
        {facts.map(t => (
          <tr key={t.field}>
            <td className="text-nowrap">
              {TEXT_FIELD[t.field]}
              {t.field === 'published' && t.present && t.source && (
                <div className="text-muted small">
                  From {t.source === 'ai_rewrite' ? 'the AI rewrite' : 'the original'}{t.editedAfterChoosing ? ', then edited' : ''}
                </div>
              )}
            </td>
            {!t.present ? (
              <td colSpan={10} className="text-muted">None</td>
            ) : (
              <>
                <td>{t.chars}</td>
                <td>{t.words}</td>
                <td>{t.lines}</td>
                <td><Count n={t.emoji} flag={false} /></td>
                <td><Count n={t.invisible} /></td>
                <td><Count n={t.unusual} /></td>
                <td><Count n={t.htmlTags} /></td>
                <td>{t.possibleScript ?<span className="support-diag__no">Yes</span> : 'No'}</td>
                <td><Count n={t.childPlaceholders} flag={false} /></td>
                <td><Count n={t.otherBracketTokens} /></td>
              </>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ImagesTable: React.FC<{ images: DiagImage[] }> = ({ images }) => (
  <div className="table-responsive">
    <table className="table table-sm support-diag__table">
      <thead>
        <tr><th>Image</th><th>Type</th><th>Uploaded</th><th>File recorded</th><th>Thumbnail</th><th>Edited</th><th>Edited thumbnail</th><th>In reports</th></tr>
      </thead>
      <tbody>
        {images.map(u => (
          <tr key={u.id}>
            <td>#{u.id}</td>
            <td>{[u.type, u.fileType].filter(Boolean).join(' · ') || '—'}</td>
            <td className="text-nowrap">{when(u.uploadedAt)}</td>
            <td><YesNo value={u.fileRecorded} /></td>
            <td><YesNo value={u.thumbnail} /></td>
            <td><YesNo value={u.edited} bad={false} />{u.edited && u.showingEdited === false ? <span className="text-muted"> (original shown)</span> : null}</td>
            <td>{u.edited ? <YesNo value={u.editedThumbnail} /> : <span className="text-muted">—</span>}</td>
            <td><YesNo value={!u.excludedFromReport} bad={false} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Page ──────────────────────────────────────────────────────────────────

const SupportDiagnostics: React.FC = () => {
  const { activeUser } = useAppState();
  const { sidebarOpen } = useOutletContext<LayoutOutletContext>();
  const navigate = useNavigate();
  const allowed = useSupportDiagnosticsAllowed();
  const [tab, setTab] = useUrlTab<Tab>('tab', TABS, 'attention');
  const [searchParams, setSearchParams] = useSearchParams();
  const openRef = searchParams.get('post');

  const [classrooms, setClassrooms] = useState<{ id: number; name: string }[]>([]);
  const [templates, setTemplates] = useState<{ id: number; name: string }[]>([]);

  // Attention
  const [days, setDays] = useState(14);
  const [attention, setAttention] = useState<DiagAttention | null>(null);
  // Posts
  const [postFilters, setPostFilters] = useState<DiagPostFilters>({});
  const [posts, setPosts] = useState<Awaited<ReturnType<typeof SupportDiagnosticsService.posts>> | null>(null);
  // Reports
  const [reportFilters, setReportFilters] = useState<DiagReportFilters>({});
  const [reports, setReports] = useState<Awaited<ReturnType<typeof SupportDiagnosticsService.reports>> | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSupport = activeUser?.isSupport === true;

  useEffect(() => {
    if (!isSupport || allowed !== true) return;
    api.get('/classrooms').then(r => setClassrooms(r.data ?? [])).catch(() => setClassrooms([]));
    ReportTemplateDataService.listTemplates()
      .then(list => setTemplates(list.filter(t => t.id !== null).map(t => ({ id: t.id as number, name: t.name }))))
      .catch(() => setTemplates([]));
  }, [isSupport, allowed]);

  const classroomName = useCallback(
    (id: number | null) => (id === null ? '—' : classrooms.find(c => c.id === id)?.name ?? `#${id}`),
    [classrooms],
  );

  const run = async <T,>(load: () => Promise<T>, set: (v: T) => void, fallback: string) => {
    setLoading(true);
    setError(null);
    try {
      set(await load());
    } catch (err) {
      setError(apiErrorMessage(err, fallback));
    } finally {
      setLoading(false);
    }
  };

  const loadAttention = useCallback(
    () => run(() => SupportDiagnosticsService.attention(days), setAttention, 'Could not load what needs attention.'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days],
  );

  // "Needs attention" loads by itself: it is where support starts.
  useEffect(() => {
    if (allowed === true && tab === 'attention' && !openRef) void loadAttention();
  }, [allowed, tab, openRef, loadAttention]);

  const openPost = (ref: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('post', ref);
    setSearchParams(next);
  };
  const closePost = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('post');
    setSearchParams(next);
  };

  if (!isSupport) return <Navigate to="/" replace />;

  const body = () => {
    if (allowed === null) return <p className="text-muted">Checking access…</p>;
    if (!allowed) {
      return (
        <div className="ivory-panel support-diag__panel">
          <h2 className="support-diag__title">Not switched on for this organisation</h2>
          <p className="mb-0">
            The organisation has not allowed support to see the status of posts and reports. An Admin can turn
            it on under <strong>Admin Dashboard → Organisation → Support access</strong>. This page updates within
            a minute of them doing so.
          </p>
        </div>
      );
    }
    if (openRef) {
      return (
        <div className="ivory-panel support-diag__panel">
          <PostDetail postRef={openRef} classroomName={classroomName} onBack={closePost} />
        </div>
      );
    }
    return (
      <div className="ivory-panel support-diag__panel">
        {error && <div className="alert alert-warning">{error}</div>}

        {tab === 'attention' && (
          <>
            <div className="support-diag__filters">
              <label className="support-diag__inline">
                Last
                <select className="form-select form-select-sm" value={days} onChange={e => setDays(Number(e.target.value))} disabled={loading}>
                  {[1, 3, 7, 14, 30].map(d => <option key={d} value={d}>{d} day{d === 1 ? '' : 's'}</option>)}
                </select>
              </label>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => void loadAttention()} disabled={loading}>
                <i className="fas fa-sync-alt" aria-hidden="true" /> Reload
              </button>
            </div>
            {loading && !attention && <p className="text-muted">Loading…</p>}
            {attention && (
              <>
                <h2 className="support-diag__subtitle">
                  Errors <span className="text-muted">({attention.errorsTotal}{attention.errorsTotal > attention.errors.length ? `, showing ${attention.errors.length}` : ''})</span>
                </h2>
                <ErrorsTable errors={attention.errors} onOpenPost={openPost} empty="No post, image, report or processing errors recorded in this period." />
                <Note text={attention.note} />

                <h2 className="support-diag__subtitle">
                  Posts with an image missing its thumbnail <span className="text-muted">({attention.postsWithIncompleteImagesTotal})</span>
                </h2>
                {attention.postsWithIncompleteImages.length === 0
                  ? <p className="text-muted mb-0">None in this period.</p>
                  : <PostsTable posts={attention.postsWithIncompleteImages} plan={attention.aiPlan} classroomName={classroomName} onOpen={openPost} />}
                <PlanLine plan={attention.aiPlan} />
              </>
            )}
          </>
        )}

        {tab === 'posts' && (
          <>
            <form
              className="support-diag__filters"
              onSubmit={e => { e.preventDefault(); void run(() => SupportDiagnosticsService.posts(postFilters), setPosts, 'Could not search posts.'); }}
            >
              <Field label="Post refs" hint="P-1234, comma separated" value={postFilters.ids} onChange={v => setPostFilters({ ...postFilters, ids: v })} />
              <Field label="From" type="date" value={postFilters.from} onChange={v => setPostFilters({ ...postFilters, from: v })} />
              <Field label="To" type="date" value={postFilters.to} onChange={v => setPostFilters({ ...postFilters, to: v })} />
              <Field label="Author" hint="User id or E-0007" value={postFilters.author} onChange={v => setPostFilters({ ...postFilters, author: v })} />
              <Field label="Child" hint="C-0042" value={postFilters.child} onChange={v => setPostFilters({ ...postFilters, child: v })} />
              <label className="support-diag__field">
                <span>Classroom</span>
                <select className="form-select form-select-sm" value={postFilters.classroom ?? ''} onChange={e => setPostFilters({ ...postFilters, classroom: e.target.value })}>
                  <option value="">Any</option>
                  {classrooms.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                </select>
              </label>
              <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>Search</button>
            </form>
            {posts && (
              posts.posts.length === 0
                ? <p className="text-muted mb-0">No posts match. Deleted posts are not shown.</p>
                : (
                  <>
                    <p className="support-diag__count">{posts.total} post{posts.total === 1 ? '' : 's'}{posts.total > posts.posts.length ? `, showing the newest ${posts.posts.length}. Narrow the search to see the rest.` : ''}</p>
                    <PostsTable posts={posts.posts} plan={posts.aiPlan} classroomName={classroomName} onOpen={openPost} />
                    <PlanLine plan={posts.aiPlan} />
                    <Note text={posts.note} />
                  </>
                )
            )}
          </>
        )}

        {tab === 'reports' && (
          <>
            <form
              className="support-diag__filters"
              onSubmit={e => { e.preventDefault(); void run(() => SupportDiagnosticsService.reports(reportFilters), setReports, 'Could not search reports.'); }}
            >
              <Field label="Report refs" hint="R-56, comma separated" value={reportFilters.ids} onChange={v => setReportFilters({ ...reportFilters, ids: v })} />
              <Field label="Period from" type="date" value={reportFilters.from} onChange={v => setReportFilters({ ...reportFilters, from: v })} />
              <Field label="Period to" type="date" value={reportFilters.to} onChange={v => setReportFilters({ ...reportFilters, to: v })} />
              <Field label="Child" hint="C-0042" value={reportFilters.child} onChange={v => setReportFilters({ ...reportFilters, child: v })} />
              <label className="support-diag__field">
                <span>Template</span>
                <select className="form-select form-select-sm" value={reportFilters.template ?? ''} onChange={e => setReportFilters({ ...reportFilters, template: e.target.value })}>
                  <option value="">Any</option>
                  {templates.map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
                </select>
              </label>
              <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>Search</button>
            </form>
            <p className="support-diag__hint">A report that failed to generate has no entry here. Search by child to see that child's report errors too.</p>
            {reports && (
              <>
                {reports.reports.length === 0
                  ? <p className="text-muted">No reports match. Deleted reports are not shown.</p>
                  : (
                    <>
                      <p className="support-diag__count">{reports.total} report{reports.total === 1 ? '' : 's'}{reports.total > reports.reports.length ? `, showing the newest ${reports.reports.length}. Narrow the search to see the rest.` : ''}</p>
                      <div className="table-responsive">
                        <table className="table table-sm support-diag__table">
                          <thead>
                            <tr><th>Ref</th><th>Child</th><th>Type</th><th>Template</th><th>Period</th><th>Created</th><th>Author</th><th>File recorded</th><th>Password</th></tr>
                          </thead>
                          <tbody>
                            {reports.reports.map((r: DiagReport) => (
                              <tr key={r.id}>
                                <td>{r.ref}</td>
                                <td>{r.child}</td>
                                <td>{r.reportType ?? '—'}</td>
                                <td>{r.templateName ?? (r.templateId ? `#${r.templateId}` : '—')}</td>
                                <td className="text-nowrap">{day(r.period.start)} – {day(r.period.end)}</td>
                                <td className="text-nowrap">{when(r.createdAt)}</td>
                                <td>{r.author?.name ?? '—'}</td>
                                <td><YesNo value={r.fileRecorded} /></td>
                                <td><YesNo value={r.passwordProtected} bad={false} /></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                {reports.errors && (
                  <>
                    <h2 className="support-diag__subtitle">Report errors for this child</h2>
                    <ErrorsTable errors={reports.errors} empty="No report errors recorded for this child." />
                    <Note text={reports.note} />
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={`filters-panel ${sidebarOpen ? 'open' : 'is-rail'}`}>
        <div className="sidebar d-flex flex-column">
          <SidebarNav className="mb-2" onNavigate={path => navigate(path)} />
        </div>
        <div />
      </div>

      <div className="top-tabs-page" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TopTabs<Tab>
          ariaLabel="Post and report status"
          active={tab}
          onChange={id => { if (openRef) closePost(); setError(null); setTab(id); }}
          tabs={[
            { id: 'attention', label: 'Needs attention', icon: 'fa-triangle-exclamation' },
            { id: 'posts', label: 'Posts', icon: 'fa-pencil-alt' },
            { id: 'reports', label: 'Reports', icon: 'fa-file-alt' },
          ]}
        />
        <div className="support-diag">
          <p className="support-diag__lead">
            The status of posts, images and reports. Never their content: no titles, text, images or report files.
            Each search and each post you open is shown to the organisation's Admins.
          </p>
          {body()}
        </div>
      </div>
    </>
  );
};

const Field: React.FC<{ label: string; hint?: string; type?: string; value?: string; onChange: (v: string) => void }> = (
  { label, hint, type = 'text', value, onChange },
) => (
  <label className="support-diag__field">
    <span>{label}</span>
    <input className="form-control form-control-sm" type={type} placeholder={hint} value={value ?? ''} onChange={e => onChange(e.target.value)} />
  </label>
);

export default SupportDiagnostics;
